import { useEffect, useState } from "react";
import firestoreService from "../../services/firestoreService";
import { useParams } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getApp } from "firebase/app";
import { useAuth } from "../auth/useAuth";

function useLectureWriting(nav, onNavChangeHandler){
    const { id } = useParams();
    const { user } = useAuth();
    const [problem, setProblem] = useState({
        question: '',
        content: '',
        correct_answer: ''
    });
    const [standard, setStandard] = useState({
        thinking: "",
        reading: "",
        expressiveness: "",
        underscore: ""
    });
    const [writingContent, setWritingContent] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [correctionResults, setCorrectionResults] = useState({
        thinking: null,
        reading: null,
        expressiveness: null,
        underscore: null,
        comprehensive: null
    });
    const [correctionHistory, setCorrectionHistory] = useState({
        thinking: [],
        reading: [],
        expressiveness: [],
        underscore: [],
        comprehensive: []
    });
    const [isOpenModal, setIsOpenModal] = useState(false);
    const [modalProps, setModalProps] = useState({
        title:"",
        message:"",
        confirmButtonName:"",
        cancelButtonName:"",
        onConfirm: null,
        onCancel: null,
    });

    // Firebase Functions 초기화 (asia-northeast3 리전 지정)
    const functions = getFunctions(getApp(), 'asia-northeast3');
    const readingComprehensionCorrection = httpsCallable(functions, 'readingComprehensionCorrection');
    const thinkingAbilityCorrection = httpsCallable(functions, 'thinkingAbilityCorrection');
    const expressionCorrection = httpsCallable(functions, 'expressionCorrection');
    const underlineCorrection = httpsCallable(functions, 'underlineCorrection');
    const comprehensiveCorrection = httpsCallable(functions, 'comprehensiveCorrection');

    const fetchProblem = async() => {
        const data = await firestoreService.getDocument(`LectureProblem`, id);
        if(data.success){
            setProblem({
                question: data.data.question || '',
                content: data.data.content || '',
                correct_answer: data.data.correct_answer || ''
            });
        } else {
            setIsOpenModal(true);
            setModalProps({
                title:"에러",
                message:"강의 문제를 가져오는 중 오류가 발생하였습니다",
                confirmButtonName:"확인",
                onConfirm:() => setIsOpenModal(false)
            });
        }
    };

    const fetchStandard = async() => {
        const data = await firestoreService.getDocument(`LectureStandard`, id);
        if(data.success){
            setStandard({
                thinking: data.data.thinking || "",
                reading: data.data.reading || "",
                expressiveness: data.data.expressiveness || "",
                underscore: data.data.underscore || ""
            });
        }
    };

    const fetchCorrectionHistory = async() => {
        try {
            // 사용자 ID 확인
            if (!user?.uid) return;

            // LectureProgress에서 첨삭 히스토리와 작성 내용 가져오기
            const docPath = `LectureProgress/${id}/LectureWriting`;
            const result = await firestoreService.getDocument(docPath, user.uid);
            console.log(result);
            
            if(result.success && result.data) {
                const data = result.data;
                
                // 첨삭 히스토리 설정
                setCorrectionHistory({
                    thinking: data.thinking_corrections || [],
                    reading: data.reading_corrections || [],
                    expressiveness: data.expression_corrections || [],
                    underscore: data.underline_corrections || [],
                    comprehensive: data.comprehensive_corrections || []
                });
                
                // 저장된 논술 내용 설정
                if (data.writing_content) {
                    setWritingContent(data.writing_content);
                }
            }
        } catch (error) {
            console.log('첨삭 히스토리를 불러올 수 없습니다:', error);
        }
    };

    useEffect(() => {
        fetchProblem();
        fetchStandard();
    }, [id]);

    // 사용자 인증 후 첨삭 히스토리 불러오기
    useEffect(() => {
        if (user?.uid) {
            fetchCorrectionHistory();
        }
    }, [user?.uid, id]);

    const handleWritingChange = (e) => {
        setWritingContent(e.target.value);
    };

    // 로딩 메시지 생성 함수
    const startLoadingAnimation = (correctionType) => {
        const messages = {
            thinking: [
                "🤔 AI가 논리적 사고를 분석하고 있습니다...",
                "🧠 사고의 흐름을 파악하는 중...",
                "💭 논리적 구조를 검토하고 있습니다...",
                "🔍 논증의 타당성을 확인하는 중...",
                "✨ 사고력 첨삭을 완성하고 있습니다..."
            ],
            reading: [
                "📖 AI가 텍스트를 꼼꼼히 읽고 있습니다...",
                "🔍 핵심 내용을 파악하는 중...",
                "📚 독해력을 분석하고 있습니다...",
                "💡 이해도를 평가하는 중...",
                "✨ 독해력 첨삭을 완성하고 있습니다..."
            ],
            expressiveness: [
                "✍️ AI가 표현력을 검토하고 있습니다...",
                "🎨 문장의 아름다움을 분석하는 중...",
                "📝 표현 기법을 평가하고 있습니다...",
                "💫 문체의 일관성을 확인하는 중...",
                "✨ 표현력 첨삭을 완성하고 있습니다..."
            ],
            underscore: [
                "🖍️ AI가 밑줄 친 부분을 분석하고 있습니다...",
                "🔎 핵심 문장을 식별하는 중...",
                "📌 중요도를 평가하고 있습니다...",
                "💎 핵심 내용을 추출하는 중...",
                "✨ 밑줄 첨삭을 완성하고 있습니다..."
            ],
            comprehensive: [
                "🎯 AI가 종합적으로 분석하고 있습니다...",
                "🔍 모든 요소를 검토하는 중...",
                "📊 전체적인 품질을 평가하고 있습니다...",
                "💎 완성도를 높이는 중...",
                "✨ 통합 첨삭을 완성하고 있습니다..."
            ]
        };

        const typeMessages = messages[correctionType] || messages.comprehensive;
        let messageIndex = 0;
        
        setLoadingMessage(typeMessages[0]);
        
        const interval = setInterval(() => {
            messageIndex = (messageIndex + 1) % typeMessages.length;
            setLoadingMessage(typeMessages[messageIndex]);
        }, 2000);

        return interval;
    };

    // 독해력 첨삭
    const performReadingCorrection = async() => {
        if (!writingContent.trim()) {
            setIsOpenModal(true);
            setModalProps({
                title: "알림",
                message: "논술을 먼저 작성해주세요.",
                confirmButtonName: "확인",
                onConfirm: () => setIsOpenModal(false)
            });
            return;
        }

        setIsLoading(true);
        const loadingInterval = startLoadingAnimation('reading');
        
        try {
            const result = await readingComprehensionCorrection({
                question: problem.question,
                userAnswer: writingContent,
                correctAnswer: problem.correct_answer,
                userId: user.uid,
                lectureId: id,
                standard: standard.reading
            });

            clearInterval(loadingInterval);
            
            if (result.data.success) {
                const correctionData = {
                    id: Date.now(),
                    content: result.data.correction,
                    timestamp: new Date(),
                    writingContent: writingContent
                };
                
                setCorrectionResults(prev => ({
                    ...prev,
                    reading: result.data.correction
                }));
                
                setCorrectionHistory(prev => ({
                    ...prev,
                    reading: [correctionData, ...prev.reading]
                }));
            } else {
                throw new Error(result.data.error);
            }
        } catch (error) {
            clearInterval(loadingInterval);
            setIsOpenModal(true);
            setModalProps({
                title: "오류",
                message: "독해력 첨삭 중 오류가 발생했습니다.",
                confirmButtonName: "확인",
                onConfirm: () => setIsOpenModal(false)
            });
        } finally {
            setIsLoading(false);
            setLoadingMessage('');
        }
    };

    // 사고력 첨삭
    const performThinkingCorrection = async() => {
        if (!writingContent.trim()) {
            setIsOpenModal(true);
            setModalProps({
                title: "알림",
                message: "논술을 먼저 작성해주세요.",
                confirmButtonName: "확인",
                onConfirm: () => setIsOpenModal(false)
            });
            return;
        }

        setIsLoading(true);
        const loadingInterval = startLoadingAnimation('thinking');
        
        try {
            const result = await thinkingAbilityCorrection({
                question: problem.question,
                userAnswer: writingContent,
                correctAnswer: problem.correct_answer,
                userId: user.uid,
                lectureId: id,
                standard: standard.thinking
            });
            
            clearInterval(loadingInterval);
            
            if (result.data.success) {
                const correctionData = {
                    id: Date.now(),
                    content: result.data.correction,
                    timestamp: new Date(),
                    writingContent: writingContent
                };
                
                setCorrectionResults(prev => ({
                    ...prev,
                    thinking: result.data.correction
                }));
                
                setCorrectionHistory(prev => ({
                    ...prev,
                    thinking: [correctionData, ...prev.thinking]
                }));
            } else {
                throw new Error(result.data.error);
            }
        } catch (error) {
            clearInterval(loadingInterval);
            setIsOpenModal(true);
            setModalProps({
                title: "오류",
                message: "사고력 첨삭 중 오류가 발생했습니다.",
                confirmButtonName: "확인",
                onConfirm: () => setIsOpenModal(false)
            });
        } finally {
            setIsLoading(false);
            setLoadingMessage('');
        }
    };

    // 표현력 첨삭
    const performExpressionCorrection = async() => {
        if (!writingContent.trim()) {
            setIsOpenModal(true);
            setModalProps({
                title: "알림",
                message: "논술을 먼저 작성해주세요.",
                confirmButtonName: "확인",
                onConfirm: () => setIsOpenModal(false)
            });
            return;
        }

        setIsLoading(true);
        const loadingInterval = startLoadingAnimation('expressiveness');
        
        try {
            const result = await expressionCorrection({
                question: problem.question,
                userAnswer: writingContent,
                correctAnswer: problem.correct_answer,
                userId: user.uid,
                lectureId: id,
                standard: standard.expressiveness
            });

            clearInterval(loadingInterval);
            
            if (result.data.success) {
                const correctionData = {
                    id: Date.now(),
                    content: result.data.correction,
                    timestamp: new Date(),
                    writingContent: writingContent
                };
                
                setCorrectionResults(prev => ({
                    ...prev,
                    expressiveness: result.data.correction
                }));
                
                setCorrectionHistory(prev => ({
                    ...prev,
                    expressiveness: [correctionData, ...prev.expressiveness]
                }));
            } else {
                throw new Error(result.data.error);
            }
        } catch (error) {
            clearInterval(loadingInterval);
            setIsOpenModal(true);
            setModalProps({
                title: "오류",
                message: "표현력 첨삭 중 오류가 발생했습니다.",
                confirmButtonName: "확인",
                onConfirm: () => setIsOpenModal(false)
            });
        } finally {
            setIsLoading(false);
            setLoadingMessage('');
        }
    };

    // 밑줄 첨삭
    const performUnderlineCorrection = async() => {
        if (!writingContent.trim()) {
            setIsOpenModal(true);
            setModalProps({
                title: "알림",
                message: "논술을 먼저 작성해주세요.",
                confirmButtonName: "확인",
                onConfirm: () => setIsOpenModal(false)
            });
            return;
        }

        setIsLoading(true);
        const loadingInterval = startLoadingAnimation('underscore');
        
        try {
            const result = await underlineCorrection({
                question: problem.question,
                userAnswer: writingContent,
                correctAnswer: problem.correct_answer,
                userId: user.uid,
                lectureId: id,
                standard: standard.underscore
            });

            clearInterval(loadingInterval);
            
            if (result.data.success) {
                const correctionData = {
                    id: Date.now(),
                    content: result.data.correction,
                    timestamp: new Date(),
                    writingContent: writingContent
                };
                
                setCorrectionResults(prev => ({
                    ...prev,
                    underscore: result.data.correction
                }));
                
                setCorrectionHistory(prev => ({
                    ...prev,
                    underscore: [correctionData, ...prev.underscore]
                }));
            } else {
                throw new Error(result.data.error);
            }
        } catch (error) {
            clearInterval(loadingInterval);
            setIsOpenModal(true);
            setModalProps({
                title: "오류",
                message: "밑줄 첨삭 중 오류가 발생했습니다.",
                confirmButtonName: "확인",
                onConfirm: () => setIsOpenModal(false)
            });
        } finally {
            setIsLoading(false);
            setLoadingMessage('');
        }
    };

    // 통합 첨삭
    const performComprehensiveCorrection = async() => {
        if (!writingContent.trim()) {
            setIsOpenModal(true);
            setModalProps({
                title: "알림",
                message: "논술을 먼저 작성해주세요.",
                confirmButtonName: "확인",
                onConfirm: () => setIsOpenModal(false)
            });
            return;
        }

        setIsLoading(true);
        const loadingInterval = startLoadingAnimation('comprehensive');
        
        try {
            // 모든 첨삭 기준을 하나의 문자열로 결합
            const allStandards = [
                standard.reading,
                standard.thinking,
                standard.expressiveness,
                standard.underscore
            ].filter(s => s.trim()).join('\n\n');

            const result = await comprehensiveCorrection({
                question: problem.question,
                userAnswer: writingContent,
                correctAnswer: problem.correct_answer,
                userId: user.uid,
                lectureId: id,
                standard: allStandards
            });

            clearInterval(loadingInterval);
            
            if (result.data.success) {
                const correctionData = {
                    id: Date.now(),
                    content: result.data.correction,
                    timestamp: new Date(),
                    writingContent: writingContent
                };
                
                setCorrectionResults(prev => ({
                    ...prev,
                    comprehensive: result.data.correction
                }));
                
                setCorrectionHistory(prev => ({
                    ...prev,
                    comprehensive: [correctionData, ...prev.comprehensive]
                }));
            } else {
                throw new Error(result.data.error);
            }
        } catch (error) {
            clearInterval(loadingInterval);
            setIsOpenModal(true);
            setModalProps({
                title: "오류",
                message: "통합 첨삭 중 오류가 발생했습니다.",
                confirmButtonName: "확인",
                onConfirm: () => setIsOpenModal(false)
            });
        } finally {
            setIsLoading(false);
            setLoadingMessage('');
        }
    };

    const performAllCorrections = async () => {
        if (!writingContent.trim()) {
            setIsOpenModal(true);
            setModalProps({
                title: "알림",
                message: "논술을 먼저 작성해주세요.",
                confirmButtonName: "확인",
                onConfirm: () => setIsOpenModal(false)
            });
            return;
        }

        const jobs = [
            {
                key: "reading",
                label: "독해력",
                call: () => readingComprehensionCorrection({
                    question: problem.question,
                    userAnswer: writingContent,
                    correctAnswer: problem.correct_answer,
                    userId: user.uid,
                    lectureId: id,
                    standard: standard.reading
                })
            },
            {
                key: "thinking",
                label: "사고력",
                call: () => thinkingAbilityCorrection({
                    question: problem.question,
                    userAnswer: writingContent,
                    correctAnswer: problem.correct_answer,
                    userId: user.uid,
                    lectureId: id,
                    standard: standard.thinking
                })
            },
            {
                key: "expressiveness",
                label: "표현력",
                call: () => expressionCorrection({
                    question: problem.question,
                    userAnswer: writingContent,
                    correctAnswer: problem.correct_answer,
                    userId: user.uid,
                    lectureId: id,
                    standard: standard.expressiveness
                })
            },
            {
                key: "underscore",
                label: "밑줄",
                call: () => underlineCorrection({
                    question: problem.question,
                    userAnswer: writingContent,
                    correctAnswer: problem.correct_answer,
                    userId: user.uid,
                    lectureId: id,
                    standard: standard.underscore
                })
            },
            {
                key: "comprehensive",
                label: "통합",
                call: () => comprehensiveCorrection({
                    question: problem.question,
                    userAnswer: writingContent,
                    correctAnswer: problem.correct_answer,
                    userId: user.uid,
                    lectureId: id,
                    standard: [standard.reading, standard.thinking, standard.expressiveness, standard.underscore]
                        .filter((item) => item && item.trim())
                        .join("\n\n")
                })
            }
        ];

        setIsLoading(true);
        const failed = [];

        for (let index = 0; index < jobs.length; index += 1) {
            const job = jobs[index];
            setLoadingMessage(`${job.label} 첨삭 중… (${index + 1}/${jobs.length})`);
            try {
                const result = await job.call();
                if (!result.data?.success) {
                    throw new Error(result.data?.error || "첨삭 실패");
                }
                const correctionData = {
                    id: Date.now() + index,
                    content: result.data.correction,
                    timestamp: new Date(),
                    writingContent
                };
                setCorrectionResults((prev) => ({
                    ...prev,
                    [job.key]: result.data.correction
                }));
                setCorrectionHistory((prev) => ({
                    ...prev,
                    [job.key]: [correctionData, ...(prev[job.key] || [])]
                }));
            } catch (error) {
                failed.push(job.label);
            }
        }

        setIsLoading(false);
        setLoadingMessage("");

        if (failed.length > 0) {
            setIsOpenModal(true);
            setModalProps({
                title: "일부 첨삭 실패",
                message: `${failed.join(", ")} 첨삭을 받지 못했습니다. 받은 결과는 탭에서 볼 수 있습니다.`,
                confirmButtonName: "확인",
                onConfirm: () => setIsOpenModal(false)
            });
        }
    };

    const submitWritingSolveData = async() => {
        try {
            // 사용자 인증 확인
            if (!user?.uid) {
                setIsOpenModal(true);
                setModalProps({
                    title: "오류",
                    message: "사용자 인증이 필요합니다. 로그인을 다시 시도해주세요.",
                    confirmButtonName: "확인",
                    onConfirm: () => setIsOpenModal(false)
                });
                return;
            }

            const docPath = `LectureProgress/${id}/LectureWriting`;
            
            // 기존 문서가 있는지 확인
            const existingData = await firestoreService.getDocument(docPath, user.uid);
            
            let result;
            if (existingData.success && existingData.data) {
                // 기존 문서가 있으면 업데이트
                const updateData = {
                    writing_content: writingContent,
                    updated_at: new Date()
                };
                
                result = await firestoreService.updateDocument(docPath, user.uid, updateData);
            } else {
                // 기존 문서가 없으면 새로 생성
                const writingData = {
                    writing_content: writingContent,
                    registered_at: new Date(),
                    updated_at: new Date()
                };
                
                result = await firestoreService.createDocument(docPath, writingData, user.uid);
            }

            console.log(result);
            
            if (result.success) {
                setIsOpenModal(true);
                setModalProps({
                    title: "성공",
                    message: "AI 자동 첨삭이 제출되었습니다.",
                    confirmButtonName: "확인",
                    onConfirm: () => setIsOpenModal(false)
                });
                
                const newNav = nav;
                newNav.completed = true;
                onNavChangeHandler(newNav);
            } else {
                throw new Error(result.error);
            }
        } catch (error) {
            setIsOpenModal(true);
            setModalProps({
                title: "오류",
                message: "논술 제출 중 오류가 발생했습니다.",
                confirmButtonName: "확인",
                onConfirm: () => setIsOpenModal(false)
            });
        }
    };

    return {
        isOpenModal,
        modalProps,
        problem,
        standard,
        writingContent,
        isLoading,
        loadingMessage,
        correctionResults,
        correctionHistory,
        handleWritingChange,
        performReadingCorrection,
        performThinkingCorrection,
        performExpressionCorrection,
        performUnderlineCorrection,
        performComprehensiveCorrection,
        performAllCorrections,
        submitWritingSolveData
    }
}

export default useLectureWriting;